import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete, Image as ImageIcon, TextFields } from '@mui/icons-material';
import { Artifact, platformApi } from '../../services/platformApi';
import {
  Crop,
  FixedBlock,
  FixedPageUnit,
  Frame,
  createFixedImageBlock,
  createFixedTextBlock,
} from './manuscriptSchema';

type Translate = (key: string) => string;

interface CanvasEditorProps {
  value: FixedPageUnit;
  onChange: (next: FixedPageUnit) => void;
  photoArtifacts: Artifact[];
  projectId?: number;
  pageWidthCm: number;
  pageHeightCm: number;
  textDirection: string;
  disabled?: boolean;
  t: Translate;
}

const clampPercent = (value: number): number => Math.min(100, Math.max(0, value));

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const textStyleField = (block: FixedBlock, key: string, fallback: unknown): unknown =>
  asRecord(block.text_style)[key] ?? fallback;

// Match the print renderer's CSS so the canvas preview and the exported page
// agree on cropping and tinting.
const filterCss = (filter: string): string =>
  filter === 'grayscale' ? 'grayscale(1)' : filter === 'sepia' ? 'sepia(1)' : 'none';

const cropClipPath = (crop: Crop): string =>
  `inset(${crop.top_percent}% ${crop.right_percent}% ${crop.bottom_percent}% ${crop.left_percent}%)`;

export const CanvasEditor: React.FC<CanvasEditorProps> = ({
  value,
  onChange,
  photoArtifacts,
  projectId,
  pageWidthCm,
  pageHeightCm,
  textDirection,
  disabled = false,
  t,
}) => {
  const isSpread = value.kind === 'spread';
  const blocks = value.blocks ?? [];
  const background = value.background ?? null;
  const [selectedId, setSelectedId] = useState<string | null>(blocks[0]?.id ?? null);

  const imageUrl = (artifactId?: number): string | null =>
    projectId && artifactId ? platformApi.getProjectArtifactDownloadUrl(projectId, artifactId) : null;

  const pageRatio = pageHeightCm > 0 ? pageWidthCm / pageHeightCm : 0.66;

  const commitBlocks = (nextBlocks: FixedBlock[]) => onChange({ ...value, blocks: nextBlocks });

  const updateBlock = (id: string, patch: Partial<FixedBlock>) =>
    commitBlocks(blocks.map((block) => (block.id === id ? { ...block, ...patch } : block)));

  const updateFrame = (id: string, patch: Partial<Frame>) => {
    const block = blocks.find((item) => item.id === id);
    if (!block) return;
    updateBlock(id, { frame: { ...block.frame, ...patch } });
  };

  const updateCrop = (id: string, patch: Partial<Crop>) => {
    const block = blocks.find((item) => item.id === id);
    if (!block) return;
    updateBlock(id, { crop: { ...block.crop, ...patch } });
  };

  const updateTextStyle = (id: string, patch: Record<string, unknown>) => {
    const block = blocks.find((item) => item.id === id);
    if (!block) return;
    updateBlock(id, { text_style: { ...asRecord(block.text_style), ...patch } });
  };

  const removeBlock = (id: string) => {
    commitBlocks(blocks.filter((block) => block.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const addBlock = (block: FixedBlock) => {
    commitBlocks([...blocks, block]);
    setSelectedId(block.id);
  };

  const setBackground = (next: FixedPageUnit['background']) => onChange({ ...value, background: next });

  const getDropPoint = (event: React.DragEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Number(clampPercent(((event.clientX - rect.left) / rect.width) * 100).toFixed(1)),
      y: Number(clampPercent(((event.clientY - rect.top) / rect.height) * 100).toFixed(1)),
    };
  };

  const dragPhoto = (event: React.DragEvent<HTMLElement>, artifactId: number) => {
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('application/talegence-photo-artifact', String(artifactId));
  };

  // Drop either a photo from the tray or an existing block onto the page
  // surface. For spreads, `onPage` scopes the block to the correct half.
  const dropOnPage = (event: React.DragEvent<HTMLElement>, onPage?: 'left' | 'right') => {
    event.preventDefault();
    if (disabled) return;
    const { x, y } = getDropPoint(event);
    const photoArtifactId = Number(event.dataTransfer.getData('application/talegence-photo-artifact'));
    if (photoArtifactId > 0) {
      const nextZ = Math.max(0, ...blocks.map((block) => block.z_index)) + 1;
      const imageBlock = createFixedImageBlock(photoArtifactId, onPage ?? 'left');
      addBlock({
        ...imageBlock,
        frame: {
          ...imageBlock.frame,
          x_percent: x,
          y_percent: y,
        },
        z_index: nextZ,
      });
      return;
    }

    const id = event.dataTransfer.getData('application/talegence-fixed-block');
    if (!id) return;
    const block = blocks.find((item) => item.id === id);
    if (!block) return;
    updateBlock(id, {
      frame: { ...block.frame, x_percent: x, y_percent: y },
      ...(onPage ? { on_page: onPage } : {}),
    });
  };

  const selected = blocks.find((block) => block.id === selectedId) ?? null;

  const blockLabel = (block: FixedBlock): string =>
    block.type === 'text' ? block.text || t('textBlock') : artifactTitle(block.artifact_id);

  const artifactTitle = (artifactId?: number): string => {
    const artifact = photoArtifacts.find((item) => item.id === artifactId);
    return artifact?.title || (artifactId ? `#${artifactId}` : t('noPhoto'));
  };

  const renderBlockBox = (block: FixedBlock) => {
    const isSelected = block.id === selectedId;
    const blockImage = block.type === 'image' ? imageUrl(block.artifact_id) : null;
    return (
      <Box
        key={block.id}
        draggable={!disabled}
        onDragStart={(event) => {
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData('application/talegence-fixed-block', block.id);
        }}
        onClick={() => setSelectedId(block.id)}
        sx={{
          position: 'absolute',
          left: `${block.frame.x_percent}%`,
          top: `${block.frame.y_percent}%`,
          width: `${block.frame.width_percent}%`,
          height: `${block.frame.height_percent}%`,
          transform: `translate(-50%, -50%) rotate(${block.frame.rotation_degrees}deg)`,
          border: '2px solid',
          borderColor: isSelected ? 'primary.main' : 'rgba(0,0,0,0.4)',
          bgcolor: block.type === 'text' ? 'rgba(255,255,255,0.7)' : 'rgba(25,118,210,0.25)',
          zIndex: block.z_index,
          cursor: disabled ? 'default' : 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          px: 0.5,
        }}
      >
        {blockImage && (
          <Box
            component="img"
            src={blockImage}
            alt=""
            draggable={false}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: filterCss(block.filter),
              clipPath: cropClipPath(block.crop),
              pointerEvents: 'none',
            }}
          />
        )}
        {!blockImage && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            {block.type === 'text' ? <TextFields fontSize="inherit" /> : <ImageIcon fontSize="inherit" />}
            <Typography variant="caption" noWrap sx={{ maxWidth: '100%' }}>
              {blockLabel(block)}
            </Typography>
          </Stack>
        )}
      </Box>
    );
  };

  // A single page surface. For spreads, `onPage` selects which blocks show
  // (its own side plus "both", matching the renderer's spread split).
  const renderPageSurface = (onPage?: 'left' | 'right') => {
    const surfaceBlocks = onPage
      ? blocks.filter((block) => block.on_page === onPage || block.on_page === 'both')
      : blocks;
    const bgColor =
      background?.type === 'color' ? background.color || '#ffffff' : background?.type === 'image' ? '#eceae4' : '#ffffff';
    const bgImage = background?.type === 'image' ? imageUrl(background.artifact_id) : null;
    // Bias the focal point to the outer edge on each spread page, matching the
    // renderer's spread split (left -> 0%, right -> 100%).
    const focalX = onPage === 'left' ? 0 : onPage === 'right' ? 100 : background?.focal_x_percent ?? 50;
    const focalY = background?.focal_y_percent ?? 50;
    return (
      <Box
        onDragOver={(event) => {
          if (disabled) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = Array.from(event.dataTransfer.types).includes('application/talegence-photo-artifact')
            ? 'copy'
            : 'move';
        }}
        onDrop={(event) => dropOnPage(event, onPage)}
        sx={{
          position: 'relative',
          flex: `0 0 ${100 / (isSpread ? 2 : 1)}%`,
          aspectRatio: `${pageRatio}`,
          bgcolor: bgColor,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {bgImage && (
          <Box
            component="img"
            src={bgImage}
            alt=""
            draggable={false}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: background?.fit === 'contain' ? 'contain' : 'cover',
              objectPosition: `${focalX}% ${focalY}%`,
              filter: filterCss(background?.filter || 'none'),
              pointerEvents: 'none',
            }}
          />
        )}
        {background?.type === 'image' && !bgImage && (
          <Typography
            variant="caption"
            sx={{ position: 'absolute', top: 4, left: 4, color: 'text.secondary', pointerEvents: 'none' }}
          >
            {t('backgroundImage')}: {artifactTitle(background.artifact_id)}
          </Typography>
        )}
        {surfaceBlocks.map(renderBlockBox)}
      </Box>
    );
  };

  return (
    <Stack spacing={1.5}>
      {/* Canvas */}
      <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
        <Box sx={{ display: 'flex', width: '100%', maxWidth: isSpread ? 520 : 300 }}>
          {isSpread ? (
            <>
              {renderPageSurface('left')}
              {renderPageSurface('right')}
            </>
          ) : (
            renderPageSurface()
          )}
        </Box>
      </Box>

      {photoArtifacts.length > 0 && (
        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary">
            {t('photos')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {photoArtifacts.map((artifact) => {
              const thumbnailUrl = imageUrl(artifact.id);
              return (
                <Tooltip key={artifact.id} title={artifact.title || `#${artifact.id}`}>
                  <Box
                    draggable={!disabled}
                    onDragStart={(event) => dragPhoto(event, artifact.id)}
                    sx={{
                      width: 72,
                      height: 56,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      overflow: 'hidden',
                      cursor: disabled ? 'default' : 'grab',
                      position: 'relative',
                      flex: '0 0 auto',
                      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
                    }}
                  >
                    {thumbnailUrl ? (
                      <Box
                        component="img"
                        src={thumbnailUrl}
                        alt=""
                        draggable={false}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    ) : (
                      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'text.secondary' }}>
                        <ImageIcon fontSize="small" />
                      </Stack>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        </Stack>
      )}

      {/* Add blocks */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          startIcon={<ImageIcon />}
          onClick={() => addBlock(createFixedImageBlock(photoArtifacts[0]?.id ?? 0))}
          disabled={disabled || photoArtifacts.length === 0}
        >
          {t('imageBlock')}
        </Button>
        <Button size="small" startIcon={<TextFields />} onClick={() => addBlock(createFixedTextBlock())} disabled={disabled}>
          {t('textBlock')}
        </Button>
      </Stack>

      {/* Background editor */}
      <Divider textAlign="left">
        <Typography variant="caption">{t('background')}</Typography>
      </Divider>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('backgroundType')}</InputLabel>
          <Select
            label={t('backgroundType')}
            value={background?.type ?? 'none'}
            onChange={(event) => {
              const type = event.target.value;
              if (type === 'none') setBackground(null);
              else if (type === 'color') setBackground({ type: 'color', color: background?.color ?? '#f4f1ea' });
              else
                setBackground({
                  type: 'image',
                  artifact_id: background?.artifact_id ?? photoArtifacts[0]?.id ?? 0,
                  fit: background?.fit ?? 'cover',
                  focal_x_percent: background?.focal_x_percent ?? 50,
                  focal_y_percent: background?.focal_y_percent ?? 50,
                  filter: 'none',
                });
            }}
            disabled={disabled}
          >
            <MenuItem value="none">{t('none')}</MenuItem>
            <MenuItem value="color">{t('color')}</MenuItem>
            <MenuItem value="image" disabled={photoArtifacts.length === 0}>
              {t('image')}
            </MenuItem>
          </Select>
        </FormControl>
        {background?.type === 'color' && (
          <TextField
            size="small"
            type="color"
            label={t('color')}
            value={background.color ?? '#f4f1ea'}
            onChange={(event) => setBackground({ ...background, color: event.target.value })}
            sx={{ width: 90 }}
            disabled={disabled}
          />
        )}
        {background?.type === 'image' && (
          <>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('photo')}</InputLabel>
              <Select
                label={t('photo')}
                value={background.artifact_id ? String(background.artifact_id) : ''}
                onChange={(event) => setBackground({ ...background, artifact_id: Number(event.target.value) })}
                disabled={disabled}
              >
                {photoArtifacts.map((artifact) => (
                  <MenuItem key={artifact.id} value={String(artifact.id)}>
                    {artifact.title || `#${artifact.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('fit')}</InputLabel>
              <Select
                label={t('fit')}
                value={background.fit ?? 'cover'}
                onChange={(event) => setBackground({ ...background, fit: event.target.value })}
                disabled={disabled}
              >
                <MenuItem value="cover">{t('fitCover')}</MenuItem>
                <MenuItem value="contain">{t('fitContain')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label={t('focalX')}
              value={background.focal_x_percent ?? 50}
              onChange={(event) => setBackground({ ...background, focal_x_percent: Number(event.target.value) })}
              sx={{ width: 100 }}
              disabled={disabled}
            />
            <TextField
              size="small"
              type="number"
              label={t('focalY')}
              value={background.focal_y_percent ?? 50}
              onChange={(event) => setBackground({ ...background, focal_y_percent: Number(event.target.value) })}
              sx={{ width: 100 }}
              disabled={disabled}
            />
          </>
        )}
      </Stack>

      {/* Selected block properties */}
      <Divider textAlign="left">
        <Typography variant="caption">{t('selectedBlock')}</Typography>
      </Divider>
      {!selected ? (
        <Typography variant="body2" color="text.secondary">
          {blocks.length === 0 ? t('canvasEmpty') : t('selectBlockHint')}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              icon={selected.type === 'text' ? <TextFields fontSize="small" /> : <ImageIcon fontSize="small" />}
              label={selected.type === 'text' ? t('textBlock') : t('imageBlock')}
              variant="outlined"
            />
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={t('removeBlock')}>
              <span>
                <IconButton size="small" color="error" onClick={() => removeBlock(selected.id)} disabled={disabled}>
                  <Delete fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {selected.type === 'image' && (
            <FormControl size="small" fullWidth disabled={disabled || photoArtifacts.length === 0}>
              <InputLabel>{t('photo')}</InputLabel>
              <Select
                label={t('photo')}
                value={selected.artifact_id ? String(selected.artifact_id) : ''}
                onChange={(event) => updateBlock(selected.id, { artifact_id: Number(event.target.value) })}
              >
                {photoArtifacts.map((artifact) => (
                  <MenuItem key={artifact.id} value={String(artifact.id)}>
                    {artifact.title || `#${artifact.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {selected.type === 'text' && (
            <>
              <TextField
                size="small"
                label={t('textBlock')}
                value={selected.text ?? ''}
                onChange={(event) => updateBlock(selected.id, { text: event.target.value })}
                fullWidth
                multiline
                disabled={disabled}
                inputProps={{ dir: textDirection }}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <TextField
                  size="small"
                  type="color"
                  label={t('color')}
                  value={String(textStyleField(selected, 'color', '#111111'))}
                  onChange={(event) => updateTextStyle(selected.id, { color: event.target.value })}
                  sx={{ width: 90 }}
                  disabled={disabled}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t('fontSize')}
                  value={Number(textStyleField(selected, 'size_pt', 18))}
                  onChange={(event) => updateTextStyle(selected.id, { size_pt: Number(event.target.value) })}
                  sx={{ width: 100 }}
                  disabled={disabled}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t('alignment')}</InputLabel>
                  <Select
                    label={t('alignment')}
                    value={String(textStyleField(selected, 'align', 'center'))}
                    onChange={(event) => updateTextStyle(selected.id, { align: event.target.value })}
                    disabled={disabled}
                  >
                    <MenuItem value="left">{t('left')}</MenuItem>
                    <MenuItem value="center">{t('center')}</MenuItem>
                    <MenuItem value="right">{t('right')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(textStyleField(selected, 'bold', false))}
                      onChange={(event) => updateTextStyle(selected.id, { bold: event.target.checked })}
                      disabled={disabled}
                    />
                  }
                  label={t('bold')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(textStyleField(selected, 'italic', false))}
                      onChange={(event) => updateTextStyle(selected.id, { italic: event.target.checked })}
                      disabled={disabled}
                    />
                  }
                  label={t('italic')}
                />
              </Stack>
            </>
          )}

          {/* Frame */}
          <Grid container spacing={1}>
            {(
              [
                ['x_percent', t('positionX')],
                ['y_percent', t('positionY')],
                ['width_percent', t('widthPercent')],
                ['height_percent', t('heightPercent')],
                ['rotation_degrees', t('rotation')],
              ] as const
            ).map(([key, label]) => (
              <Grid item xs={6} sm={4} md={2.4} key={key}>
                <TextField
                  size="small"
                  type="number"
                  label={label}
                  value={selected.frame[key]}
                  onChange={(event) => updateFrame(selected.id, { [key]: Number(event.target.value) } as Partial<Frame>)}
                  fullWidth
                  disabled={disabled}
                />
              </Grid>
            ))}
          </Grid>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              type="number"
              label={t('layer')}
              value={selected.z_index}
              onChange={(event) => updateBlock(selected.id, { z_index: Number(event.target.value) })}
              sx={{ width: 100 }}
              disabled={disabled}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>{t('imageFilter')}</InputLabel>
              <Select
                label={t('imageFilter')}
                value={selected.filter}
                onChange={(event) => updateBlock(selected.id, { filter: event.target.value })}
                disabled={disabled}
              >
                <MenuItem value="none">{t('filterNone')}</MenuItem>
                <MenuItem value="grayscale">{t('filterGrayscale')}</MenuItem>
                <MenuItem value="sepia">{t('filterSepia')}</MenuItem>
              </Select>
            </FormControl>
            {isSpread && (
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>{t('onPage')}</InputLabel>
                <Select
                  label={t('onPage')}
                  value={selected.on_page}
                  onChange={(event) => updateBlock(selected.id, { on_page: event.target.value as FixedBlock['on_page'] })}
                  disabled={disabled}
                >
                  <MenuItem value="left">{t('sideLeft')}</MenuItem>
                  <MenuItem value="right">{t('sideRight')}</MenuItem>
                  <MenuItem value="both">{t('bothPages')}</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>

          {/* Crop (images only) */}
          {selected.type === 'image' && (
            <Grid container spacing={1}>
              {(
                [
                  ['top_percent', t('cropTopPercent')],
                  ['right_percent', t('cropRightPercent')],
                  ['bottom_percent', t('cropBottomPercent')],
                  ['left_percent', t('cropLeftPercent')],
                ] as const
              ).map(([key, label]) => (
                <Grid item xs={6} sm={3} key={key}>
                  <TextField
                    size="small"
                    type="number"
                    label={label}
                    value={selected.crop[key]}
                    onChange={(event) => updateCrop(selected.id, { [key]: Number(event.target.value) } as Partial<Crop>)}
                    fullWidth
                    disabled={disabled}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      )}
    </Stack>
  );
};
